export type ActivityReturnContext = Readonly<{
  destination: string;
  label: "Today" | "Calendar" | "Library";
}>;

export type ActivityReturnState = Readonly<{
  activityReturnTo: string;
}>;

const relativeUrlBase = "https://learnflow.invalid";

export function createActivityReturnState(destination: string): ActivityReturnState | undefined {
  const context = parseActivityReturnContext(destination);
  return context ? { activityReturnTo: context.destination } : undefined;
}

export function resolveActivityReturnContext(
  routeState: unknown,
  storedLibraryContext: string,
): ActivityReturnContext {
  const routeDestination =
    isRecord(routeState) && typeof routeState.activityReturnTo === "string"
      ? routeState.activityReturnTo
      : null;
  const routeContext = parseActivityReturnContext(routeDestination);
  if (routeContext) return routeContext;

  const libraryContext = parseActivityReturnContext(storedLibraryContext);
  return libraryContext?.label === "Library"
    ? libraryContext
    : { destination: "/library", label: "Library" };
}

export function parseActivityReturnContext(value: unknown): ActivityReturnContext | null {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return null;

  let url: URL;
  try {
    url = new URL(value, relativeUrlBase);
  } catch {
    return null;
  }

  const destination = `${url.pathname}${url.search}`;
  if (url.origin !== relativeUrlBase || url.hash || destination !== value) return null;
  if (destination === "/today") return { destination, label: "Today" };
  if (url.pathname === "/calendar") return { destination, label: "Calendar" };
  if (url.pathname === "/library") return { destination, label: "Library" };
  return null;
}

export function parseActivityDetailReturnDestination(
  value: unknown,
  activityId: string,
): string | null {
  if (
    typeof value !== "string" ||
    !activityId ||
    !value.startsWith("/") ||
    value.startsWith("//")
  ) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(value, relativeUrlBase);
  } catch {
    return null;
  }

  const destination = `${url.pathname}${url.search}`;
  return url.origin === relativeUrlBase &&
    !url.hash &&
    destination === value &&
    url.pathname === `/activities/${activityId}`
    ? destination
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
